import { formatCalendarDate } from "@/lib/date";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { trpc } from "@/hooks/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { InvSupplierId } from "@brewbase/db/types";
import { Button } from "@/components/ui/button";
import { FileUploader, FileInput } from "@/components/ui/file-uploader";
import { useState } from "react";
import { toast } from "sonner";
import { RequireCapability } from "@/components/require-capability";

export const Route = createFileRoute("/settings/inventory/suppliers/$id")({
  params: {
    parse: ({ id }) => ({ id: z.coerce.number().parse(id) }),
    stringify: ({ id }) => ({ id: String(id) }),
  },
  component: () => (
    <RequireCapability cap="inventory:manage">
      <SupplierDetailPage />
    </RequireCapability>
  ),
});

function SupplierDetailPage() {
  const { id } = Route.useParams();
  const supplierId = id as InvSupplierId;
  const { data: supplier } = trpc.inventory.supplier.get.useQuery(supplierId);
  const utils = trpc.useUtils();
  const approve = trpc.inventory.supplier.approve.useMutation({
    onSuccess: () => utils.inventory.supplier.get.invalidate(supplierId),
  });
  const reject = trpc.inventory.supplier.reject.useMutation({
    onSuccess: () => utils.inventory.supplier.get.invalidate(supplierId),
  });
  const [docFiles, setDocFiles] = useState<File[] | null>(null);
  const uploadSupplierDoc = async () => {
    if (!docFiles || docFiles.length === 0) return;
    const fd = new FormData();
    fd.append("target", "supplier");
    fd.append("target_id", String(supplierId));
    fd.append("certificate_type_id", String(1));
    fd.append("file", docFiles[0]);
    const res = await fetch("/api/certificates/upload", {
      method: "POST",
      body: fd,
    });
    if (!res.ok) {
      toast.error("Failed to upload document");
    } else {
      toast.success("Document uploaded");
      setDocFiles(null);
      utils.inventory.certificate.list.invalidate({ supplier_id: supplierId });
    }
  };
  if (!supplier) return null;
  return (
    <div className="container mx-auto px-4 py-6 space-y-4">
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="certificates">Certificates</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle>{supplier.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>Status: {supplier.status}</div>
              <div>URL: {supplier.url ?? "-"}</div>
              <div>Contact: {supplier.contact_info ?? "-"}</div>
              <div className="flex gap-2 pt-2">
                <Button
                  size="sm"
                  onClick={() => approve.mutate({ id: supplierId })}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => reject.mutate({ id: supplierId })}
                >
                  Reject
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle>Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-muted-foreground mb-2">
                Link required docs here (F11.3, SDS, etc.).
              </div>
              <div className="flex items-center gap-2">
                <FileUploader
                  value={docFiles}
                  onValueChange={setDocFiles}
                  dropzoneOptions={{ multiple: false }}
                >
                  <FileInput />
                </FileUploader>
                <Button
                  size="sm"
                  onClick={uploadSupplierDoc}
                  disabled={!docFiles || docFiles.length === 0}
                >
                  Upload
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="certificates">
          <Card>
            <CardHeader>
              <CardTitle>Certificates</CardTitle>
            </CardHeader>
            <CardContent>
              <SupplierCertificates supplierId={supplierId} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SupplierCertificates({ supplierId }: { supplierId: InvSupplierId }) {
  const { data: certs = [] } = trpc.inventory.certificate.list.useQuery({
    supplier_id: supplierId,
  });
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-muted">
          <th className="text-left p-2">Type</th>
          <th className="text-left p-2">Issuing Body</th>
          <th className="text-left p-2">Valid From</th>
          <th className="text-left p-2">Valid Until</th>
        </tr>
      </thead>
      <tbody>
        {certs.map((c: any) => (
          <tr key={c.id} className="border-t">
            <td className="p-2">{c.certificate_type_id}</td>
            <td className="p-2">{c.issuing_body ?? "-"}</td>
            <td className="p-2">
              {c.valid_from ? formatCalendarDate(c.valid_from) : "-"}
            </td>
            <td className="p-2">
              {c.valid_until
                ? formatCalendarDate(c.valid_until)
                : "-"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
